import { create } from 'zustand'
import type { User } from '@vestolio/shared'
import { authApi } from '../api/auth'
import { getStorage } from '../api/client'

interface AuthState {
  user: User | null
  loading: boolean
  isAuthenticated: boolean

  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void

  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

/**
 * Platform-agnostic auth store.
 * Works on both web and mobile since it uses the storage adapter pattern.
 */
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  isAuthenticated: false,

  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setLoading: (loading) => set({ loading }),

  login: async (email: string, password: string) => {
    set({ loading: true })
    try {
      const response = await authApi.login({ email, password })
      const storage = getStorage()

      await storage.setItem('token', response.tokens.token)
      await storage.setItem('refreshToken', response.tokens.refreshToken)

      set({ user: response.user, isAuthenticated: true, loading: false })
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  logout: async () => {
    await authApi.logout()
    set({ user: null, isAuthenticated: false })
  },

  checkAuth: async () => {
    try {
      const storage = getStorage()
      const token = await storage.getItem('token')
      if (token) {
        const response = await authApi.me()
        set({ user: response.data!, isAuthenticated: true, loading: false })
      } else {
        set({ loading: false })
      }
    } catch {
      set({ user: null, isAuthenticated: false, loading: false })
    }
  },
}))